import { observer } from 'mobx-react-lite';
import {
  type MouseEvent,
  type TouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { cn } from '@workspace/ui/lib/utils';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import type { ProductViewProps } from '~/pages/Product/Product';

import { ProductDELightbox } from './ProductDELightbox';

type Props = Pick<
  ProductViewProps,
  'isBundle' | 'bundleImages' | 'currentItem' | 'product' | 'isLoading'
>;

const SWIPE_THRESHOLD = 40;
const TAP_TOLERANCE = 6;
const HERO_IMAGE_WIDTH = 1024;
const THUMB_IMAGE_WIDTH = 160;

/**
 * Product gallery of the DubaiExpress product page: an 80px thumbnail strip
 * next to a 520px hero on desktop, a square hero with dots on mobile. Slides
 * move as a track; a horizontal swipe by finger or mouse flips them.
 */
const ProductDEGalleryView = ({
  isBundle,
  bundleImages,
  currentItem,
  product,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
    heroRef.current?.focus({ preventScroll: true });
  }, []);

  const images = isBundle
    ? bundleImages
    : (product.selectedOffer?.images ?? []);
  const imagesKey = images.join('|');

  useEffect(() => {
    setActiveIndex(0);
  }, [imagesKey]);

  if (isLoading) {
    return (
      <div className="aspect-square desktop:aspect-auto desktop:h-[520px] w-full overflow-hidden rounded-[10px]">
        <Skeleton height="100%" containerClassName="block h-full" />
      </div>
    );
  }

  if (
    (isBundle && !bundleImages?.length) ||
    !currentItem?.images.length ||
    !product.selectedOffer ||
    !images.length
  ) {
    return null;
  }

  const count = images.length;
  const hasThumbs = count > 1;

  const swipe = (x: number, y: number) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start) {
      return;
    }
    const dx = x - start.x;
    const dy = y - start.y;
    if (Math.abs(dx) <= TAP_TOLERANCE && Math.abs(dy) <= TAP_TOLERANCE) {
      setIsFullscreen(true);
      return;
    }
    if (
      count < 2 ||
      Math.abs(dx) < SWIPE_THRESHOLD ||
      Math.abs(dx) < Math.abs(dy)
    ) {
      return;
    }
    setActiveIndex((index) => (index + (dx < 0 ? 1 : count - 1)) % count);
  };

  const handleTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    pointerStart.current =
      touch && event.touches.length === 1
        ? { x: touch.clientX, y: touch.clientY }
        : null;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    if (touch) {
      swipe(touch.clientX, touch.clientY);
    }
  };

  const handleMouseDown = (event: MouseEvent) => {
    if (event.button !== 0) {
      return;
    }
    pointerStart.current = { x: event.clientX, y: event.clientY };
    event.preventDefault();
  };

  const handleMouseUp = (event: MouseEvent) =>
    swipe(event.clientX, event.clientY);

  const handleMouseLeave = () => {
    pointerStart.current = null;
  };

  return (
    <div
      className={cn(
        'flex desktop:h-[520px] flex-col-reverse gap-2',
        hasThumbs &&
          'desktop:grid desktop:grid-cols-[80px_minmax(0,1fr)] desktop:gap-3',
      )}
    >
      {hasThumbs && (
        <div className="desktop:relative desktop:min-h-0">
          <div className="scrollbar-hide desktop:absolute desktop:inset-0 desktop:flex hidden desktop:flex-col desktop:gap-2 desktop:overflow-y-auto">
            {images.map((image, index) => (
              <button
                key={image}
                type="button"
                aria-label={`${index + 1}`}
                aria-pressed={index === activeIndex}
                onClick={() => setActiveIndex(index)}
                className={cn(
                  'box-border size-20 shrink-0 cursor-pointer rounded-[8px] border border-[#EDEDED] border-solid bg-white p-1.5',
                  index === activeIndex && 'border-2 border-[#E94E1A]',
                )}
              >
                <CloudflareResponseImage
                  src={image}
                  srcParams={{ width: THUMB_IMAGE_WIDTH }}
                  srcSetParams={defaultSrcSetParams}
                  sizes="80px"
                  alt=""
                  loading="eager"
                  fetchPriority="low"
                  draggable={false}
                  className="size-full object-contain"
                />
              </button>
            ))}
          </div>
          <div className="flex desktop:hidden items-center justify-center gap-1.5 py-0.5">
            {images.map((image, index) => (
              <button
                key={image}
                type="button"
                aria-label={`${index + 1}`}
                aria-pressed={index === activeIndex}
                onClick={() => setActiveIndex(index)}
                className={cn(
                  'h-1.5 shrink-0 cursor-pointer rounded-[3px] border-0 p-0 transition-[width,background-color] duration-200 ease-[cubic-bezier(.22,1,.36,1)]',
                  index === activeIndex
                    ? 'w-[18px] bg-[#E94E1A]'
                    : 'w-1.5 bg-[#D9D9D9]',
                )}
              />
            ))}
          </div>
        </div>
      )}
      <div
        ref={heroRef}
        role="button"
        tabIndex={0}
        aria-label={t('productDE:photoOpen')}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsFullscreen(true);
          }
        }}
        className={cn(
          'relative aspect-square desktop:aspect-auto desktop:h-full w-full select-none overflow-hidden rounded-[10px] bg-[#F7F7F7]',
          count > 1 ? 'cursor-grab' : 'cursor-zoom-in',
        )}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="absolute inset-0 flex transition-transform duration-[320ms] ease-[cubic-bezier(.22,1,.36,1)]"
          style={{ transform: `translateX(${-100 * activeIndex}%)` }}
        >
          {images.map((image, index) => (
            <div key={image} className="size-full shrink-0 bg-white p-3">
              <CloudflareResponseImage
                src={image}
                srcParams={{ width: HERO_IMAGE_WIDTH }}
                srcSetParams={defaultSrcSetParams}
                sizes="(min-width: 1025px) 418px, 100vw"
                alt={product.data?.name ?? ''}
                loading="eager"
                fetchPriority={index === 0 ? 'high' : 'low'}
                draggable={false}
                className="size-full object-contain"
              />
            </div>
          ))}
        </div>
      </div>
      {isFullscreen && (
        <ProductDELightbox
          images={images}
          index={activeIndex}
          title={product.data?.name ?? ''}
          onIndexChange={setActiveIndex}
          onClose={closeFullscreen}
        />
      )}
    </div>
  );
};

export const ProductDEGallery = observer(ProductDEGalleryView);
